import React, { useState, useEffect, useRef } from 'react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useToast } from '../../hooks/useToast';
import { logger } from '../../utils/logger';
import { EnhancedAIAnalysisService, type EnhancedAIAnalysisData, type ChatSession, type ChatMessage } from '../../services/EnhancedAIAnalysisService';
import type { AIAnalysisRequest, CompanyMetrics } from '../../types/market';

interface EnhancedAIAnalysisProps {
    ticker: string;
    companyName: string;
    currentPrice: number;
    marketCap?: number;
    sector?: string;
    financialMetrics?: Partial<CompanyMetrics>;
    className?: string;
}

export const EnhancedAIAnalysis: React.FC<EnhancedAIAnalysisProps> = ({
    ticker,
    companyName,
    currentPrice,
    marketCap,
    sector,
    financialMetrics,
    className = '',
}) => {
    const [analysis, setAnalysis] = useState<EnhancedAIAnalysisData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'analysis' | 'chat'>('analysis');

    // Chat state
    const [chatSession, setChatSession] = useState<ChatSession | null>(null);
    const [chatMessage, setChatMessage] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const aiServiceRef = useRef<EnhancedAIAnalysisService | null>(null);
    if (!aiServiceRef.current) {
        aiServiceRef.current = new EnhancedAIAnalysisService();
    }
    const aiService = aiServiceRef.current;
    const { showToast } = useToast();

    const loadAnalysis = async (forceRefresh = false) => {
        setLoading(true);
        setError(null);

        try {
            const request: AIAnalysisRequest = {
                ticker,
                companyName,
                currentPrice,
                marketCap,
                sector,
                financialMetrics,
            };

            const result = forceRefresh
                ? await aiService.refreshEnhancedAnalysis(request)
                : await aiService.generateEnhancedAnalysis(request);
            setAnalysis(result);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load enhanced AI analysis';
            setError(errorMessage);
            logger.error('Enhanced AI Analysis error:', err);
        } finally {
            setLoading(false);
        }
    };

    const initializeChatSession = async () => {
        try {
            logger.debug('Initializing chat session for ticker:', ticker);
            const session = await aiService.startChatSession(ticker);
            logger.debug('Chat session created:', session.id);
            setChatSession(session);
        } catch (err) {
            logger.error('Failed to initialize chat session:', err);
        }
    };

    const sendChatMessage = async () => {
        if (!chatMessage.trim() || chatLoading) return;

        setChatLoading(true);
        const userMessage = chatMessage.trim();
        setChatMessage('');

        try {
            // If no chat session exists, create one
            let currentSession = chatSession;
            if (!currentSession) {
                logger.debug('No chat session found, creating new one for ticker:', ticker);
                currentSession = await aiService.startChatSession(ticker);
                setChatSession(currentSession);
            }

            logger.debug('Sending message to session:', currentSession.id);
            await aiService.sendChatMessage(currentSession.id, userMessage);
            const updatedSession = aiService.getChatSession(currentSession.id);
            if (updatedSession) {
                logger.debug(`Updated session with ${updatedSession.messages.length} messages`);
                setChatSession(updatedSession);
            }
        } catch (err) {
            logger.error('Failed to send chat message:', err);
            showToast('Failed to send message. Please try again.', 'error');
        } finally {
            setChatLoading(false);
        }
    };

    const clearChatHistory = () => {
        aiService.clearChatHistory(ticker);
        setChatSession(null);
        initializeChatSession();
        showToast('Chat history cleared', 'success');
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendChatMessage();
        }
    };

    useEffect(() => {
        loadAnalysis();
        // Initialize chat session in background
        initializeChatSession();
    }, [ticker]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatSession?.messages]);

    const getRecommendationColor = (recommendation: string) => {
        switch (recommendation) {
            case 'BUY':
                return 'text-green-600 bg-green-50 border-green-200';
            case 'SELL':
                return 'text-red-600 bg-red-50 border-red-200';
            case 'HOLD':
                return 'text-yellow-600 bg-yellow-50 border-yellow-200';
            case 'NOT_ENOUGH_INFO':
                return 'text-gray-600 bg-gray-50 border-gray-200';
            default:
                return 'text-yellow-600 bg-yellow-50 border-yellow-200';
        }
    };

    const getRecommendationText = (recommendation: string) => {
        switch (recommendation) {
            case 'NOT_ENOUGH_INFO':
                return 'Not Enough Information';
            default:
                return recommendation;
        }
    };

    const formatPriceTarget = (priceTarget: string) => {
        if (priceTarget.toLowerCase().includes('unavailable') || priceTarget.toLowerCase().includes('not available')) {
            return 'Not Available';
        }
        return priceTarget;
    };

    const formatChatMessage = (content: string) => {
        try {
            // Remove reference citations like [1], [2], [1][2], etc.
            let formatted = content.replace(/\[\d+\](\[\d+\])*/g, '');

            // Convert **bold** to HTML bold (non-greedy)
            formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

            // Convert *italic* to HTML italic (simple approach to avoid lookbehind issues)
            formatted = formatted.replace(/\*([^*\n]+?)\*/g, '<em>$1</em>');

            // Convert line breaks to <br> tags
            formatted = formatted.replace(/\n/g, '<br>');

            // Clean up multiple spaces but preserve single spaces
            formatted = formatted.replace(/\s{2,}/g, ' ').trim();

            // Remove any remaining markdown-style formatting
            formatted = formatted.replace(/#{1,6}\s/g, ''); // Remove headers
            formatted = formatted.replace(/`([^`]+)`/g, '<code>$1</code>'); // Convert inline code

            return formatted;
        } catch (error) {
            logger.warn('Error formatting chat message:', error);
            // Return original content if formatting fails
            return content;
        }
    };

    const formatTimeAgo = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

        if (diffInHours < 1) {
            return 'Just now';
        } else if (diffInHours < 24) {
            return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
        } else {
            const diffInDays = Math.floor(diffInHours / 24);
            return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
        }
    };

    if (loading && !analysis) {
        return (
            <div className={`bg-white rounded-lg shadow-md p-4 ${className}`}>
                <div className="flex items-center justify-center py-6">
                    <LoadingSpinner size="lg" />
                    <span className="ml-3 text-gray-600">Generating comprehensive AI analysis...</span>
                </div>
            </div>
        );
    }

    if (error && !analysis) {
        return (
            <div className={`bg-white rounded-lg shadow-md p-4 ${className}`}>
                <div className="text-center py-6">
                    <div className="text-red-600 mb-4">
                        <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm">{error}</p>
                    </div>
                    <button
                        onClick={() => loadAnalysis()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (!analysis) {
        return null;
    }

    return (
        <>
            <style>{`
                .chat-message strong {
                    font-weight: 600;
                    color: #374151;
                }
                .chat-message em {
                    font-style: italic;
                    color: #6B7280;
                }
                .chat-message code {
                    background-color: #F3F4F6;
                    padding: 2px 4px;
                    border-radius: 3px;
                    font-family: monospace;
                    font-size: 0.875em;
                }
            `}</style>
            <div className={`bg-white rounded-lg shadow-md ${className}`}>
                {/* Header with Tabs */}
                <div className="p-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Enhanced AI Analysis</h3>
                        <button
                            onClick={() => loadAnalysis(true)}
                            disabled={loading}
                            className="flex items-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                        >
                            <svg className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            {loading ? 'Refreshing...' : 'Refresh'}
                        </button>
                    </div>

                    {/* Tab Navigation */}
                    <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                        <button
                            onClick={() => setActiveTab('analysis')}
                            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${activeTab === 'analysis'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            Analysis
                        </button>
                        <button
                            onClick={() => setActiveTab('chat')}
                            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${activeTab === 'chat'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            Ask Questions
                            {chatSession?.messages && chatSession.messages.length > 0 && (
                                <span className="ml-1 bg-blue-500 text-white text-xs rounded-full px-2 py-0.5">
                                    {chatSession.messages.length}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-3">
                    {activeTab === 'analysis' ? (
                        <div className="space-y-2">
                            {/* Recommendation Badge */}
                            <div className="flex justify-center">
                                <div className={`inline-flex items-center px-4 py-2 rounded-full border-2 font-semibold text-sm ${getRecommendationColor(analysis.recommendation)}`}>
                                    <span className="w-2 h-2 rounded-full bg-current mr-2"></span>
                                    {getRecommendationText(analysis.recommendation)}
                                </div>
                            </div>

                            {/* Price Target */}
                            <div className="text-center border-b border-gray-100 pb-2">
                                <div className={`text-xl font-bold ${formatPriceTarget(analysis.price_target) === 'Not Available'
                                    ? 'text-gray-500'
                                    : 'text-gray-900'
                                    }`}>
                                    {formatPriceTarget(analysis.price_target)}
                                </div>
                                <p className="text-xs text-gray-500">12-Month Price Target</p>
                            </div>

                            {/* Analysis Sections */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Fundamental Valuation */}
                                <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-gray-900 flex items-center border-b border-gray-100 pb-1">
                                        <svg className="w-4 h-4 mr-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                        </svg>
                                        Fundamental Valuation
                                    </h4>
                                    <div className="ai-analysis-content text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: analysis.fundamental_valuation.replace(/\n/g, '<br>') }}></div>
                                </div>

                                {/* Technical Analysis */}
                                <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-gray-900 flex items-center border-b border-gray-100 pb-1">
                                        <svg className="w-4 h-4 mr-1 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                        </svg>
                                        Technical Analysis
                                    </h4>
                                    <div className="ai-analysis-content text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: analysis.technical_analysis.replace(/\n/g, '<br>') }}></div>
                                </div>

                                {/* Sentiment & Catalysts */}
                                <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-gray-900 flex items-center border-b border-gray-100 pb-1">
                                        <svg className="w-4 h-4 mr-1 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                        </svg>
                                        Sentiment & Catalysts
                                    </h4>
                                    <div className="ai-analysis-content text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: analysis.sentiment_catalysts.replace(/\n/g, '<br>') }}></div>
                                </div>

                                {/* Risk Factors */}
                                <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-gray-900 flex items-center border-b border-gray-100 pb-1">
                                        <svg className="w-4 h-4 mr-1 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                        Risk Factors
                                    </h4>
                                    <div className="ai-analysis-content text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: analysis.risk_factors.replace(/\n/g, '<br>') }}></div>
                                </div>
                            </div>

                            {/* Investment Thesis */}
                            <div className="space-y-2 border-t border-gray-200 pt-4">
                                <h4 className="text-sm font-medium text-gray-900 flex items-center border-b border-gray-100 pb-1">
                                    <svg className="w-4 h-4 mr-1 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                    </svg>
                                    Investment Thesis
                                </h4>
                                <div className="ai-analysis-content text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: analysis.investment_thesis.replace(/\n/g, '<br>') }}></div>
                            </div>
                        </div>
                    ) : (
                        /* Chat Interface */
                        <div className="space-y-4">
                            {/* Chat Header with Clear Button */}
                            {chatSession?.messages && chatSession.messages.length > 0 && (
                                <div className="flex justify-between items-center">
                                    <p className="text-sm text-gray-600">
                                        {chatSession.messages.length} message{chatSession.messages.length !== 1 ? 's' : ''} in this conversation
                                    </p>
                                    <button
                                        onClick={clearChatHistory}
                                        className="text-xs text-red-600 hover:text-red-800 underline"
                                    >
                                        Clear History
                                    </button>
                                </div>
                            )}

                            {/* Chat Messages */}
                            <div className="h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 space-y-4">
                                {chatSession?.messages.length === 0 ? (
                                    <div className="text-center text-gray-500 py-8">
                                        <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                        </svg>
                                        <p className="text-lg font-medium mb-2">Ask me anything about {ticker}</p>
                                        <p className="text-sm">Get detailed insights about valuation, risks, technical analysis, and more.</p>
                                        <div className="mt-4 flex flex-wrap justify-center gap-2">
                                            <button
                                                onClick={() => setChatMessage('What is the fair value of this stock?')}
                                                className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                                            >
                                                Fair value?
                                            </button>
                                            <button
                                                onClick={() => setChatMessage('What are the main risks?')}
                                                className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition-colors"
                                            >
                                                Main risks?
                                            </button>
                                            <button
                                                onClick={() => setChatMessage('When should I buy or sell?')}
                                                className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
                                            >
                                                Timing?
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    chatSession?.messages.map((message) => (
                                        <div
                                            key={message.id}
                                            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                        >
                                            <div
                                                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${message.role === 'user'
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-100 text-gray-900'
                                                    }`}
                                            >
                                                {message.role === 'assistant' ? (
                                                    <div
                                                        className="text-sm leading-relaxed chat-message"
                                                        dangerouslySetInnerHTML={{
                                                            __html: formatChatMessage(message.content)
                                                        }}
                                                        style={{
                                                            wordBreak: 'break-word'
                                                        }}
                                                    />
                                                ) : (
                                                    <p className="text-sm leading-relaxed">{message.content}</p>
                                                )}
                                                <p className={`text-xs mt-1 ${message.role === 'user' ? 'text-blue-200' : 'text-gray-500'
                                                    }`}>
                                                    {formatTimeAgo(message.timestamp)}
                                                </p>
                                            </div>
                                        </div>
                                    ))
                                )}
                                {chatLoading && (
                                    <div className="flex justify-start">
                                        <div className="bg-gray-100 text-gray-900 px-4 py-2 rounded-lg">
                                            <LoadingSpinner size="sm" />
                                            <span className="ml-2 text-sm">Analyzing...</span>
                                        </div>
                                    </div>
                                )}
                                <div ref={chatEndRef} />
                            </div>

                            {/* Chat Input */}
                            <div className="flex space-x-2">
                                <input
                                    type="text"
                                    value={chatMessage}
                                    onChange={(e) => setChatMessage(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder={`Ask about ${ticker} analysis...`}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    disabled={chatLoading}
                                />
                                <button
                                    onClick={sendChatMessage}
                                    disabled={!chatMessage.trim() || chatLoading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Disclaimer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                    <div className="flex items-start space-x-2">
                        <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <div className="text-sm text-gray-600">
                            <p className="font-medium mb-1">Important Disclaimer</p>
                            <p>
                                This AI-generated analysis uses fundamental valuation, technical analysis, and sentiment assessment for informational purposes only.
                                Not financial advice. Always conduct your own research and consult qualified financial advisors before making investment decisions.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};